import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferList, { OfferListAction, OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import { isBedbankOffer, isFlashHotelOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import { pluralizeToString } from 'lib/string/pluralize'
import React, { useMemo } from 'react'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import BannerForSuperSlimTile from 'components/OfferList/OfferListTiles/SearchOfferTiles/Banners/BannerForSuperSlimTile'
import useOffer from 'hooks/Offers/useOffer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import useShouldHighlight from 'hooks/OfferPage/useShouldHighlightTile'
interface Props {
  filters: App.OfferListFilters;
}

function HotelSearchNearbyPropertyOfferList(props: Props) {
  const { filters } = props

  const showSlimMobile = useSlimMobileSearch()
  const hotelList = useOfferList(filters)

  const propertyOffer = useAppSelector(state => state.offer.offers[hotelList.offerIds[0]] || state.offer.bedbankOffers[hotelList.offerIds[0]])
  const propertyOfferLoading = useAppSelector(state => state.offer.offersLoading[hotelList.offerIds[0]])

  const nearbyFilters = useMemo(() => ({ ...filters, searchNearby: true }), [filters])
  const nearbyOfferList = useOfferList(nearbyFilters)

  const propertyLocation = useMemo(() => {
    if (propertyOffer) {
      return isBedbankOffer(propertyOffer) ? propertyOffer.property.address.city : propertyOffer.property.geoData.locality
    }
  }, [propertyOffer])

  // AB Test Settings
  //  show banner option when super slim AB test is enabled for LPC and LPP
  let showSuperSlimAbTestBanner = false
  let bannerVariant : 1 | 2 | 3 | undefined = 3
  let discountPercent : number | undefined = undefined
  const isHighlight = useShouldHighlight({ offer: propertyOffer, filters })
  const isSuperSlimOfferTileABTestEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.superSlimTilesForOfferListPage, isHighlight)
  const [secondOffer, ,] = useOffer(nearbyOfferList.offerIds[0], {
    requireSummaryOnly: true,
  })

  if (propertyOffer) {
    const isPlcOrLpp = isBedbankOffer(propertyOffer) || isLpcHotelOffer(propertyOffer)
    showSuperSlimAbTestBanner = isSuperSlimOfferTileABTestEnabled && isPlcOrLpp
  }

  if (secondOffer && isFlashHotelOffer(secondOffer) && secondOffer.lowestPricePackage) {
    discountPercent = Math.round((secondOffer.lowestPricePackage.value - secondOffer.lowestPricePackage.price) / secondOffer.lowestPricePackage.value * 100)
    bannerVariant = 1
    if (discountPercent < MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) {
      bannerVariant = 3
    }
  } else {
    bannerVariant = 3
  }

  // AB Test Settings End
  const overrideNearbyOfferList = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: nearbyOfferList,
  }), [nearbyOfferList])

  const extras: Array<OfferListV2Item> = [{
    id: 'nearby-heading',
    position: 'start',
    element: <>
      {!propertyOffer && !propertyOfferLoading && <MessageBanner align="center" icon={<LineInfoCircleIcon />} kind="informational-primary" description="This offer is no longer available" />}
      {showSuperSlimAbTestBanner && <BannerForSuperSlimTile destName={propertyLocation} variant={bannerVariant} discountPercent={discountPercent}/>}
      {!showSuperSlimAbTestBanner && <Heading variant="heading4">
        {pluralizeToString('similar offer', nearbyOfferList.offerIds.length)}{' '}
        found{propertyLocation ? ` near ${propertyLocation}` : ' nearby'}
      </Heading>}
    </>,
    show: 'hasOffers',
  }]

  return <OfferList
    fullWidth={showSlimMobile}
    interstitials={extras}
    filters={nearbyFilters}
    tileStyle="search"
    extraOfferListAndAction={overrideNearbyOfferList}
  />
}

export default HotelSearchNearbyPropertyOfferList
